import type { StorefrontCatalog, StorefrontPricingBasis } from '@erp/shared';
import { useEffect } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { customerApi } from '../../lib/customerApi.js';
import { useCustomerAuthStore } from '../../lib/customerAuthStore.js';
import { publicApi } from '../../lib/publicApi.js';

import { reconcileCartCurrency } from './cartPricing.js';

/**
 * The shopper's PRICING BASIS on the public storefront: which customer group
 * (price list) the server prices them with and — decisively — the CURRENCY that
 * group's prices are denominated in.
 *
 * Every money-shaped thing on the storefront keys off `currencyCode` here rather
 * than the store-wide `site.currencyCode`: a customer whose group is priced in
 * USD sees, carts and pays USD; guests and ungrouped customers keep the store
 * currency. Prices are NEVER converted client-side — the server returns the
 * group's stored prices and stamps the currency on every payload
 * (`StorefrontCatalog.pricing`, `StorefrontProduct{Summary,Detail}.currencyCode`);
 * we only follow it.
 */

/** Cache key: a basis belongs to ONE store AND ONE shopper identity. */
const basisKey = (slug: string, partyId: string | null): string => `${slug}|${partyId ?? 'guest'}`;

/** The identity the server will price for: the signed-in party, else guest. */
function currentPartyId(): string | null {
  const s = useCustomerAuthStore.getState();
  return s.accessToken && s.customer?.partyId ? s.customer.partyId : null;
}

interface BasisState {
  key: string | null;
  basis: StorefrontPricingBasis | null;
  /** Identity whose resolve failed — display falls back, the cart is left alone. */
  failedKey: string | null;
  remember(key: string, basis: StorefrontPricingBasis): void;
  markFailed(key: string): void;
}

/**
 * Session-scoped so it lives exactly as long as the customer session it belongs
 * to (customerAuthStore is sessionStorage too) — a reloaded /cart then knows the
 * shopper's currency without a flash of store-currency prices.
 */
const useBasisStore = create<BasisState>()(
  persist(
    (set) => ({
      key: null,
      basis: null,
      failedKey: null,
      remember: (key, basis) => set({ key, basis, failedKey: null }),
      markFailed: (key) => set({ failedKey: key }),
    }),
    { name: 'erp-store-pricing-basis', storage: createJSONStorage(() => sessionStorage) },
  ),
);

/** One in-flight resolve per identity — many components may ask at once. */
const inflight = new Set<string>();

const guestBasis = (currencyCode: string): StorefrontPricingBasis => ({
  priceListId: null,
  priceListName: null,
  currencyCode,
});

/**
 * Bind a recorder to WHO we are about to ask as, then feed it the `pricing` block
 * the catalogue response came back with. Pages that already call /products hand
 * it straight over, so the shopper's currency usually costs no extra round-trip.
 *
 * Capturing the identity BEFORE the request matters: a response issued as a guest
 * that lands after a sign-in must not be filed under the customer's name.
 */
export function pricingRecorder(slug: string): (pricing: StorefrontPricingBasis | undefined) => void {
  const key = basisKey(slug, currentPartyId());
  return (pricing) => {
    if (!pricing) return;
    const st = useBasisStore.getState();
    const same = st.key === key
      && st.basis?.currencyCode === pricing.currencyCode
      && st.basis.priceListId === pricing.priceListId;
    if (!same || st.failedKey !== null) st.remember(key, pricing);
    applyToCart(slug, key, pricing.currencyCode);
  };
}

/**
 * A freshly-learnt basis also settles the cart, wherever the shopper happens to
 * be: waiting for them to open /cart would let the next quick-add silently drop
 * every line from the old basis. Skipped when the answer belongs to a shopper we
 * are no longer (a response that landed after a sign-in/out).
 */
function applyToCart(slug: string, key: string, currency: string): void {
  if (key !== basisKey(slug, currentPartyId())) return;
  reconcileCartCurrency(slug, currency);
}

/**
 * Ask the server which group/currency it prices this shopper in. `?limit=1`
 * keeps the payload tiny — only the `pricing` block matters.
 */
function resolveBasis(key: string, slug: string, storeCurrency: string): void {
  if (inflight.has(key)) return;
  inflight.add(key);
  void publicApi()
    .get<StorefrontCatalog>(`/${slug}/products`, { params: { limit: 1 } })
    .then((r) => {
      // A store with nothing published omits `pricing` — the store currency stands.
      const basis = r.data.pricing ?? guestBasis(storeCurrency);
      useBasisStore.getState().remember(key, basis);
      applyToCart(slug, key, basis.currencyCode);
    })
    .catch(() => {
      // Never trap the shopper behind a spinner: display falls back to the store
      // currency, the cart is left untouched, and the next mount retries.
      useBasisStore.getState().markFailed(key);
    })
    .finally(() => inflight.delete(key));
}

/** At most one session-renewal ping in flight. */
let renewing = false;

/**
 * Keep the customer session alive while they shop.
 *
 * The storefront client is anonymous-first and does NOT refresh tokens, so an
 * access token that lapses mid-browse would quietly drop a group customer back
 * to public prices — while the checkout (which does refresh) still charged their
 * group's. One ping at the portal client, whose 401 handler refreshes and
 * retries, keeps display and charge on the same footing. A dead refresh cookie
 * signs them out, which is exactly what the server already thinks.
 */
function renewCustomerSession(): void {
  if (renewing) return;
  renewing = true;
  void customerApi().get('/me').catch(() => undefined).finally(() => { renewing = false; });
}

export interface BuyerPricing extends StorefrontPricingBasis {
  /** The shopper's group/currency is known — safe to re-price or prune the cart. */
  authoritative: boolean;
  /** False only while a signed-in shopper's group is still being fetched. */
  resolved: boolean;
  /** The store's own currency — delivery fees and the minimum order live in it. */
  storeCurrencyCode: string;
  /** The shopper's group prices in a currency other than the store's. */
  foreign: boolean;
}

/**
 * The current shopper's group + currency. Guests resolve instantly (store
 * currency, no request); a signed-in customer's group is fetched once per
 * session and kept fresh by every catalogue response.
 */
export function useBuyerPricing(): BuyerPricing {
  const { slug, site } = useStorefront();
  const partyId = useCustomerAuthStore((s) => (s.accessToken ? s.customer?.partyId ?? null : null));
  const expiresAt = useCustomerAuthStore((s) => s.expiresAt);
  const cachedKey = useBasisStore((s) => s.key);
  const cachedBasis = useBasisStore((s) => s.basis);
  const failedKey = useBasisStore((s) => s.failedKey);
  const key = basisKey(slug, partyId);
  const cached = cachedKey === key ? cachedBasis : null;

  useEffect(() => {
    // Guests are the store currency by definition — nothing to ask the server.
    if (!partyId || cached) return;
    resolveBasis(key, slug, site.currencyCode);
  }, [key, slug, partyId, cached, site.currencyCode]);

  useEffect(() => {
    // A lapsed access token still rides along on catalogue reads, where it is
    // simply ignored — renew it so their prices stay theirs.
    if (partyId && expiresAt != null && expiresAt <= Date.now()) renewCustomerSession();
  }, [partyId, expiresAt]);

  const basis = cached ?? guestBasis(site.currencyCode);
  const authoritative = Boolean(cached) || !partyId;
  return {
    ...basis,
    authoritative,
    resolved: authoritative || failedKey === key,
    storeCurrencyCode: site.currencyCode,
    foreign: basis.currencyCode !== site.currencyCode,
  };
}
